import { type MenuAction, useParentLayerId } from '@dataroom/ui';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
  createContext,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
} from 'react';
import { currentPlatform } from '../keys';
import { type CommandRegistry, commandRegistry, commandShortcuts } from './registry';
import type { CommandContribution, Scope } from './types';

const RegistryContext = createContext<CommandRegistry>(commandRegistry);

export function CommandRegistryProvider({
  registry,
  children,
}: {
  registry: CommandRegistry;
  children: ReactNode;
}) {
  return <RegistryContext.Provider value={registry}>{children}</RegistryContext.Provider>;
}

export function useCommandRegistry(): CommandRegistry {
  return useContext(RegistryContext);
}

/** Re-render on registry changes (registrations, activations). */
export function useRegistryVersion(given?: CommandRegistry): number {
  const fromContext = useCommandRegistry();
  const registry = given ?? fromContext;
  return useSyncExternalStore(
    (l) => registry.subscribe(l),
    () => registry.getVersion(),
    () => 0,
  );
}

export type UseCommandScopeOptions<Ctx> = {
  /** The context the scope's commands run with (read when a key is pressed; no re-registration). */
  ctx: Ctx;
  /** The scope's element (focus containment, preference when several scopes of a level are active). */
  element?: RefObject<Element | null>;
  /**
   * `within`: active only while focus is inside `element`. `within-or-idle`: also while nothing has
   * focus (document body). Default `mounted`.
   */
  focus?: 'mounted' | 'within' | 'within-or-idle';
  /** Deactivate without unmounting. */
  enabled?: boolean;
};

/**
 * Activate `scope` while the component is mounted. Inside a layer the scope belongs to that layer, so
 * its commands run only while the layer is on top.
 */
export function useCommandScope<Ctx>(scope: Scope<Ctx>, options: UseCommandScopeOptions<Ctx>): void {
  const registry = useCommandRegistry();
  const layerId = useParentLayerId();
  const latest = useRef(options);
  latest.current = options;
  const { enabled = true, focus = 'mounted' } = options;
  useEffect(() => {
    if (!enabled) return;
    return registry.activate(scope, {
      ctx: () => latest.current.ctx,
      element: () => latest.current.element?.current ?? null,
      focus,
      layerId,
    });
  }, [registry, scope, enabled, focus, layerId]);
}

const NAVIGATION_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End']);

/**
 * `onKeyDown` for an editor area inside a dialog or drawer. The dialog primitive stops the arrow, Home
 * and End keys at its popup (they belong to composite widgets), so the window listener never sees
 * them and the scope's nudge commands stay silent. This handler runs before the popup's and hands
 * exactly those keys to the registry; the event is then `preventDefault`ed, so nothing runs twice.
 */
export function useNavigationKeyRelay(): (event: ReactKeyboardEvent) => void {
  const registry = useCommandRegistry();
  return (event) => {
    if (!NAVIGATION_KEYS.has(event.key)) return;
    registry.dispatch(event.nativeEvent);
  };
}

/** Register a contribution while mounted (packages and tests; app contributions load by glob). */
export function useRegisterCommands(contribution: CommandContribution): void {
  const registry = useCommandRegistry();
  useEffect(() => registry.add(contribution), [registry, contribution]);
}

/** A command's live state for buttons and tooltips. */
export function useCommand(id: string): {
  run: () => boolean;
  enabled: boolean;
  shortcut?: string;
  title: string;
} {
  const registry = useCommandRegistry();
  useRegistryVersion(registry);
  const command = registry.getCommand(id);
  return {
    run: () => registry.run(id, 'api'),
    enabled: registry.canRun(id),
    shortcut: command ? commandShortcuts(command, currentPlatform())[0] : undefined,
    title: command?.title() ?? id,
  };
}

/**
 * A `Menu` / `ContextMenu` item for a command: title, icon, shortcut column and disabled state come
 * from the registry, selecting runs it.
 */
export function menuItemFromCommand(
  id: string,
  overrides: Partial<Omit<MenuAction, 'onSelect'>> = {},
  registry: CommandRegistry = commandRegistry,
): MenuAction {
  const command = registry.getCommand(id);
  if (!command) throw new Error(`Unknown command "${id}"`);
  return {
    label: command.title(),
    ...(command.icon ? { icon: command.icon } : {}),
    ...(commandShortcuts(command)[0] ? { shortcut: commandShortcuts(command)[0] } : {}),
    disabled: !registry.canRun(id),
    ...overrides,
    onSelect: () => {
      registry.run(id, 'menu');
    },
  };
}
